import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertTriangle, BookOpen, Calculator, Copy, Eraser, Play, Sigma } from "lucide-react";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import { integrate, exprToTex, type IntegrationResult } from "@/lib/math/integration";
import { evaluate as mjsEvaluate } from "mathjs";
import { STANDARD_INTEGRALS } from "@/data/standardIntegrals";

type Bilingual = { en: string; bn: string };
type Mode = "indefinite" | "definite";

const STR = {
  h1: { en: "Integral Calculator", bn: "ইন্টিগ্রাল ক্যালকুলেটর" },
  desc: {
    en: "Compute indefinite and definite integrals with step-by-step solutions. Built for HSC and university calculus.",
    bn: "নির্দিষ্ট ও অনির্দিষ্ট অখণ্ডন ধাপে ধাপে সমাধান সহ। HSC ও বিশ্ববিদ্যালয় ক্যালকুলাসের জন্য।",
  },
  mode: { en: "Type", bn: "ধরন" },
  indefinite: { en: "Indefinite ∫ f(x) dx", bn: "অনির্দিষ্ট ∫ f(x) dx" },
  definite: { en: "Definite ∫ₐᵇ f(x) dx", bn: "নির্দিষ্ট ∫ₐᵇ f(x) dx" },
  function: { en: "Function f(x)", bn: "ফাংশন f(x)" },
  variable: { en: "Variable", bn: "চলক" },
  lower: { en: "Lower limit a", bn: "নিম্নসীমা a" },
  upper: { en: "Upper limit b", bn: "ঊর্ধ্বসীমা b" },
  preview: { en: "Preview", bn: "প্রিভিউ" },
  compute: { en: "Integrate", bn: "অখণ্ডন করুন" },
  clear: { en: "Clear", bn: "মুছুন" },
  showSteps: { en: "Show steps", bn: "ধাপ দেখান" },
  result: { en: "Result", bn: "ফলাফল" },
  steps: { en: "Steps", bn: "ধাপসমূহ" },
  examples: { en: "Try an example", bn: "উদাহরণ চেষ্টা করুন" },
  reference: { en: "Standard integrals", bn: "প্রমাণিত অখণ্ডন তালিকা" },
  copyResult: { en: "Copy result", bn: "ফলাফল কপি করুন" },
  numerical: { en: "Computed numerically (Simpson's rule)", bn: "সংখ্যাত্মকভাবে নির্ণীত (সিম্পসন'স রুল)" },
  noResult: {
    en: "Could not solve symbolically. Try a simpler form, or supply limits to use numerical integration.",
    bn: "প্রতীকীভাবে সমাধান করা যায়নি। সরল রূপে চেষ্টা করুন, অথবা সীমা দিন (সংখ্যাত্মক অখণ্ডনের জন্য)।",
  },
  honesty: {
    en: "This tool is for learning calculus. Use it to check your work and understand the steps. Do NOT use during an exam — that violates academic integrity rules.",
    bn: "এটি ক্যালকুলাস শেখার জন্য। নিজের কাজ যাচাই ও ধাপ বুঝতে ব্যবহার করুন। পরীক্ষার সময় ব্যবহার করবেন না — এটি একাডেমিক সততার নিয়মের বিরুদ্ধে।",
  },
};

const EXAMPLES: { en: string; bn: string; expr: string; mode: Mode; a?: string; b?: string }[] = [
  { en: "∫ x dx", bn: "∫ x dx", expr: "x", mode: "indefinite" },
  { en: "∫ x² dx", bn: "∫ x² dx", expr: "x^2", mode: "indefinite" },
  { en: "∫ x³ dx", bn: "∫ x³ dx", expr: "x^3", mode: "indefinite" },
  { en: "∫ 1/x dx", bn: "∫ 1/x dx", expr: "1/x", mode: "indefinite" },
  { en: "∫ e^x dx", bn: "∫ e^x dx", expr: "exp(x)", mode: "indefinite" },
  { en: "∫ sin(x) dx", bn: "∫ sin(x) dx", expr: "sin(x)", mode: "indefinite" },
  { en: "∫ cos(x) dx", bn: "∫ cos(x) dx", expr: "cos(x)", mode: "indefinite" },
  { en: "∫ (x² + 3x − 5) dx", bn: "∫ (x² + 3x − 5) dx", expr: "x^2 + 3x - 5", mode: "indefinite" },
  { en: "∫₀^π sin(x) dx", bn: "∫₀^π sin(x) dx", expr: "sin(x)", mode: "definite", a: "0", b: "pi" },
  { en: "∫₀¹ x² dx", bn: "∫₀¹ x² dx", expr: "x^2", mode: "definite", a: "0", b: "1" },
  { en: "∫₁^e (1/x) dx", bn: "∫₁^e (1/x) dx", expr: "1/x", mode: "definite", a: "1", b: "e" },
];

const KEYBOARD_KEYS: { label: string; insert: string }[] = [
  { label: "x", insert: "x" }, { label: "x²", insert: "^2" }, { label: "x³", insert: "^3" },
  { label: "x^n", insert: "^" }, { label: "(", insert: "(" }, { label: ")", insert: ")" },
  { label: "+", insert: "+" }, { label: "−", insert: "-" }, { label: "×", insert: "*" }, { label: "÷", insert: "/" },
  { label: "√", insert: "sqrt(" }, { label: "e^x", insert: "exp(x)" }, { label: "ln", insert: "log(" },
  { label: "log", insert: "log10(" }, { label: "sin", insert: "sin(" }, { label: "cos", insert: "cos(" },
  { label: "tan", insert: "tan(" }, { label: "π", insert: "pi" }, { label: "e", insert: "e" },
];

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "How do I integrate a function?", bn: "ফাংশন কীভাবে অখণ্ডন করতে হয়?" },
    a: {
      en: "Type your function of x — for example x^2 or sin(x). Choose Indefinite for the antiderivative (with + C), or Definite to evaluate ∫ₐᵇ f(x) dx between two limits. The calculator shows each rule applied along the way.",
      bn: "x-এর কোনো ফাংশন লিখুন — যেমন x^2 বা sin(x)। অনির্দিষ্ট নির্বাচন করলে অখণ্ডিত (+ C সহ) দেখাবে, নির্দিষ্ট নির্বাচন করলে দুই সীমার মধ্যে ∫ₐᵇ f(x) dx নির্ণয় করবে। প্রতিটি ধাপ ব্যাখ্যা সহ দেখানো হবে।",
    },
  },
  {
    q: { en: "What is the + C in indefinite integrals?", bn: "অনির্দিষ্ট অখণ্ডনে + C কী?" },
    a: {
      en: "C is the constant of integration. Because the derivative of any constant is 0, infinitely many functions share the same derivative — they all differ only by a constant. We write + C to represent that family of antiderivatives.",
      bn: "C হলো অখণ্ডনের ধ্রুবক। যেকোনো ধ্রুবকের অবকলজ ০, তাই একই অবকলজবিশিষ্ট অসীম সংখ্যক ফাংশন থাকে — তারা শুধু একটি ধ্রুবক দিয়ে পার্থক্য। সেই পরিবারকে বোঝাতে + C লেখা হয়।",
    },
  },
  {
    q: { en: "Definite vs indefinite integrals?", bn: "নির্দিষ্ট ও অনির্দিষ্ট অখণ্ডনের পার্থক্য?" },
    a: {
      en: "An indefinite integral returns a function (the antiderivative) plus a constant. A definite integral returns a number — the signed area under the curve between two limits a and b, computed as F(b) − F(a) by the Fundamental Theorem of Calculus.",
      bn: "অনির্দিষ্ট অখণ্ডন একটি ফাংশন (অখণ্ডিত) ও ধ্রুবক দেয়। নির্দিষ্ট অখণ্ডন একটি সংখ্যা দেয় — দুই সীমা a ও b এর মধ্যে বক্ররেখার চিহ্নযুক্ত ক্ষেত্রফল, ক্যালকুলাসের মৌলিক উপপাদ্য অনুযায়ী F(b) − F(a)।",
    },
  },
  {
    q: { en: "Which integration techniques does this support?", bn: "কোন কোন কৌশল সমর্থিত?" },
    a: {
      en: "The engine handles the power rule, constants and constant multiples, sums and differences (linearity), exponentials and a^x, basic trig (sin, cos, tan, sec, csc, cot), reciprocal 1/x, and basic linear u-substitution for f(ax + b). For other forms it falls back to numerical Simpson's rule when limits are given.",
      bn: "ইঞ্জিন পাওয়ার রুল, ধ্রুবক ও ধ্রুবক গুণনিয়ম, যোগ-বিয়োগ (লিনিয়ারিটি), সূচকীয় ও a^x, ত্রিকোণমিতি (sin, cos, tan, sec, csc, cot), 1/x, এবং f(ax+b) এর জন্য রৈখিক u-প্রতিস্থাপন সমর্থন করে। অন্য ক্ষেত্রে সীমা দিলে সিম্পসন'স রুলে সংখ্যাত্মক ফলাফল দেয়।",
    },
  },
  {
    q: { en: "How accurate is the numerical answer?", bn: "সংখ্যাত্মক উত্তর কতটা নির্ভুল?" },
    a: {
      en: "We use composite Simpson's rule with n = 1000 sub-intervals. For smooth integrands the error is typically below 1e-9 of the result. Functions with singularities, sharp peaks, or asymptotes inside [a, b] may produce larger error or NaN.",
      bn: "n = ১০০০ অংশসহ যৌগিক সিম্পসন'স রুল ব্যবহৃত হয়। মসৃণ ফাংশনে সাধারণত ত্রুটি ফলাফলের 1e-9 এর নিচে। [a, b] এর ভেতর অসংজ্ঞা/অ্যাসিম্পটোট থাকলে ত্রুটি বাড়তে পারে।",
    },
  },
  {
    q: { en: "ইন্টিগ্রেশন কীভাবে করতে হয়?", bn: "ইন্টিগ্রেশন কীভাবে করতে হয়?" },
    a: {
      en: "x-এর ফাংশন লিখুন (যেমন x^2 বা sin(x)), 'অখণ্ডন করুন' চাপুন। প্রতিটি প্রয়োগকৃত নিয়ম ও ফলাফল KaTeX-এ পরিষ্কারভাবে দেখানো হবে।",
      bn: "x-এর ফাংশন লিখুন (যেমন x^2 বা sin(x)), 'অখণ্ডন করুন' চাপুন। প্রতিটি প্রয়োগকৃত নিয়ম ও ফলাফল KaTeX-এ পরিষ্কারভাবে দেখানো হবে।",
    },
  },
];

export const Route = createFileRoute("/integral-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Integral Calculator — Definite & Indefinite",
      description:
        "Free integral calculator. Compute indefinite and definite integrals with step-by-step solutions. For HSC and university calculus. ইন্টিগ্রাল ক্যালকুলেটর।",
      path: "/integral-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Integral Calculator", path: "/integral-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: IntegralCalculatorPage,
});

function safeTex(src: string, fallback = ""): string {
  try { return src; } catch { return fallback; }
}

function IntegralCalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];

  const [mode, setMode] = useState<Mode>("indefinite");
  const [expr, setExpr] = useState("x^2");
  const [variable, setVariable] = useState("x");
  const [aStr, setAStr] = useState("0");
  const [bStr, setBStr] = useState("1");
  const [showSteps, setShowSteps] = useState(true);
  const [result, setResult] = useState<IntegrationResult | null>(null);

  const previewTex = useMemo(() => {
    try {
      const f = exprToTex(expr || "0");
      if (mode === "indefinite") return `\\int ${f} \\, d${variable}`;
      return `\\int_{${aStr || "a"}}^{${bStr || "b"}} ${f} \\, d${variable}`;
    } catch { return expr; }
  }, [expr, mode, variable, aStr, bStr]);

  const onCompute = () => {
    const opts: { variable: string; a?: number; b?: number } = { variable };
    if (mode === "definite") {
      try {
        opts.a = Number(mjsEvaluate(aStr || "0"));
        opts.b = Number(mjsEvaluate(bStr || "0"));
      } catch {
        opts.a = Number(aStr);
        opts.b = Number(bStr);
      }
    }
    const r = integrate(expr, opts);
    setResult(r);
  };

  const onClear = () => { setExpr(""); setResult(null); };
  const insert = (frag: string) => setExpr((s) => s + frag);
  const useExample = (ex: typeof EXAMPLES[number]) => {
    setMode(ex.mode);
    setExpr(ex.expr);
    if (ex.a !== undefined) setAStr(ex.a);
    if (ex.b !== undefined) setBStr(ex.b);
    setResult(null);
  };
  const copy = () => {
    if (!result?.ok) return;
    if (typeof navigator !== "undefined") {
      const txt = mode === "indefinite"
        ? `∫ ${expr} d${variable} = ${result.display}`
        : `∫[${aStr} to ${bStr}] ${expr} d${variable} = ${result.display}`;
      navigator.clipboard?.writeText(txt);
    }
  };

  return (
    <CalculatorLayout
      slug="integral-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Sigma"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Honesty disclaimer */}
      <div className={cn(
        "mb-5 flex items-start gap-3 rounded-xl border border-yellow-400/40 bg-yellow-400/10 p-3 text-xs text-foreground/90",
        lang === "bn" && "font-bn",
      )}>
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-yellow-500" />
        <p>{tr("honesty")}</p>
      </div>

      {/* Mode selector */}
      <div className="space-y-1.5">
        <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("mode")}</label>
        <div className="flex flex-wrap gap-1.5">
          {(["indefinite", "definite"] as const).map((m) => {
            const active = mode === m;
            return (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium transition",
                  active ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card text-muted-foreground hover:bg-muted",
                  lang === "bn" && "font-bn",
                )}
              >
                {m === "indefinite" ? tr("indefinite") : tr("definite")}
              </button>
            );
          })}
        </div>
      </div>

      {/* Function input */}
      <section className="mt-4 space-y-2">
        <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("function")}</label>
        <input
          type="text"
          value={expr}
          onChange={(e) => setExpr(e.target.value)}
          placeholder="x^2"
          spellCheck={false}
          className="h-11 w-full rounded-md border border-border bg-card px-3 font-mono text-base outline-none focus:ring-2 focus:ring-accent/60"
        />
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <div>
            <label className={cn("mb-1 block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>{tr("variable")}</label>
            <input
              type="text"
              value={variable}
              onChange={(e) => setVariable(e.target.value.replace(/[^a-zA-Z]/g, "").slice(0, 1) || "x")}
              className="h-9 w-full rounded-md border border-border bg-card px-2 font-mono text-sm"
            />
          </div>
          {mode === "definite" && (
            <>
              <div>
                <label className={cn("mb-1 block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>{tr("lower")}</label>
                <input
                  type="text"
                  value={aStr}
                  onChange={(e) => setAStr(e.target.value)}
                  className="h-9 w-full rounded-md border border-border bg-card px-2 font-mono text-sm"
                />
              </div>
              <div>
                <label className={cn("mb-1 block text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>{tr("upper")}</label>
                <input
                  type="text"
                  value={bStr}
                  onChange={(e) => setBStr(e.target.value)}
                  className="h-9 w-full rounded-md border border-border bg-card px-2 font-mono text-sm"
                />
              </div>
            </>
          )}
        </div>

        {/* Math keyboard */}
        <div className="flex flex-wrap gap-1">
          {KEYBOARD_KEYS.map((k) => (
            <button
              key={k.label}
              type="button"
              onClick={() => insert(k.insert)}
              className="h-8 min-w-8 rounded border border-border bg-card px-2 font-mono text-xs hover:bg-muted"
            >
              {k.label}
            </button>
          ))}
        </div>
      </section>

      {/* Preview */}
      <section className="mt-4 rounded-xl border border-border bg-card/40 p-3">
        <div className={cn("mb-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{tr("preview")}</div>
        <div className="overflow-x-auto">
          <BlockMath math={safeTex(previewTex, expr)} />
        </div>
      </section>

      {/* Action row */}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={onCompute} className="bg-accent text-accent-foreground hover:bg-accent/90">
          <Play size={14} className="mr-1.5" /> {tr("compute")}
        </Button>
        <Button variant="outline" onClick={onClear}>
          <Eraser size={14} className="mr-1.5" /> {tr("clear")}
        </Button>
        <label className="ml-auto flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={showSteps} onChange={(e) => setShowSteps(e.target.checked)} className="accent-accent" />
          <span className={lang === "bn" ? "font-bn" : ""}>{tr("showSteps")}</span>
        </label>
      </div>

      {/* Result */}
      {result && (
        <motion.section
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-5 rounded-xl border border-border bg-card p-4"
        >
          <div className="flex items-center justify-between">
            <h3 className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>{tr("result")}</h3>
            {result.ok && (
              <Button variant="ghost" size="sm" onClick={copy}>
                <Copy size={12} className="mr-1" /> {tr("copyResult")}
              </Button>
            )}
          </div>
          {!result.ok && (
            <p className={cn("mt-2 text-sm text-destructive", lang === "bn" && "font-bn")}>
              {result.error?.[lang] || tr("noResult")}
            </p>
          )}
          {result.ok && (
            <>
              <div className="mt-2 overflow-x-auto rounded bg-muted/40 p-3">
                <BlockMath math={`${previewTex} = ${result.displayTex || result.display || ""}`} />
              </div>
              {result.numerical && (
                <p className={cn("mt-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                  {tr("numerical")}
                </p>
              )}
              {showSteps && result.steps.length > 0 && (
                <div className="mt-3">
                  <div className={cn("mb-1.5 text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>{tr("steps")}</div>
                  <ol className="space-y-2">
                    {result.steps.map((s, i) => (
                      <li key={i} className="rounded-md border border-border bg-background p-2 text-sm">
                        <div className="flex items-start gap-2">
                          <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/15 text-[11px] font-semibold text-accent">{i + 1}</span>
                          <div className="min-w-0 flex-1">
                            <p className={cn(lang === "bn" && "font-bn")}>{s[lang]}</p>
                            {s.expr && (
                              <div className="mt-1 overflow-x-auto">
                                <InlineMath math={s.expr} />
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </>
          )}
        </motion.section>
      )}

      {/* Examples */}
      <section className="mt-6">
        <h3 className={cn("mb-2 flex items-center gap-1.5 text-sm font-medium", lang === "bn" && "font-bn")}>
          <BookOpen size={14} /> {tr("examples")}
        </h3>
        <div className="flex flex-wrap gap-1.5">
          {EXAMPLES.map((ex, i) => (
            <button
              key={i}
              type="button"
              onClick={() => useExample(ex)}
              className="rounded-full border border-border bg-card px-3 py-1 text-xs font-medium hover:border-accent hover:bg-accent/10"
            >
              {ex[lang]}
            </button>
          ))}
        </div>
      </section>

      {/* Standard integrals reference */}
      <section className="mt-6">
        <h3 className={cn("mb-2 flex items-center gap-1.5 text-sm font-medium", lang === "bn" && "font-bn")}>
          <Sigma size={14} /> {tr("reference")}
        </h3>
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">f(x)</th>
                <th className="px-3 py-2 text-left">∫ f(x) dx</th>
              </tr>
            </thead>
            <tbody>
              {STANDARD_INTEGRALS.map((row, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-3 py-2"><InlineMath math={row.fTex} /></td>
                  <td className="px-3 py-2"><InlineMath math={row.FTex + " + C"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Educational content */}
      <article className={cn("mt-8 space-y-5 text-sm leading-relaxed", lang === "bn" && "font-bn")}>
        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "অখণ্ডন কী?" : "What is integration?"}</h2>
          <p className="text-muted-foreground">
            {lang === "bn"
              ? "অখণ্ডন হলো অবকলনের বিপরীত ক্রিয়া। কোনো ফাংশন F(x) এর অবকলজ যদি f(x) হয়, তবে F(x) কে f(x) এর অখণ্ডিত (antiderivative) বলে এবং লেখা হয় ∫f(x) dx = F(x) + C। জ্যামিতিকভাবে নির্দিষ্ট অখণ্ডন বক্ররেখার নিচের চিহ্নযুক্ত ক্ষেত্রফল দেয়।"
              : "Integration is the reverse of differentiation. If F(x) has derivative f(x), then F(x) is an antiderivative of f(x), written ∫f(x) dx = F(x) + C. Geometrically, a definite integral gives the signed area under the curve."}
          </p>
        </section>
        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "পাওয়ার রুল" : "The power rule"}</h2>
          <BlockMath math={"\\int x^n \\, dx = \\frac{x^{n+1}}{n+1} + C \\quad (n \\neq -1)"} />
          <p className="mt-1 text-muted-foreground">
            {lang === "bn"
              ? "n = −১ এর ক্ষেত্রে: ∫(1/x) dx = ln|x| + C।"
              : "Special case n = −1: ∫(1/x) dx = ln|x| + C."}
          </p>
        </section>
        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "u-প্রতিস্থাপন" : "u-Substitution"}</h2>
          <p className="text-muted-foreground">
            {lang === "bn"
              ? "যদি অখণ্ড্য রূপ f(g(x))·g'(x) হয়, তবে u = g(x) ধরে du = g'(x) dx — অখণ্ডনটি ∫f(u) du তে রূপান্তরিত হয়। আমাদের টুল রৈখিক ক্ষেত্র (g(x) = ax + b) স্বয়ংক্রিয়ভাবে শনাক্ত করে।"
              : "If the integrand has the form f(g(x))·g'(x), set u = g(x) so du = g'(x) dx. The integral becomes ∫f(u) du. Our engine handles the linear case g(x) = ax + b automatically."}
          </p>
        </section>
        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "অংশভাগে অখণ্ডন" : "Integration by parts"}</h2>
          <BlockMath math={"\\int u \\, dv = uv - \\int v \\, du"} />
          <p className="mt-1 text-muted-foreground">
            {lang === "bn"
              ? "দুটি ফাংশনের গুণফলের জন্য (যেমন x·sin(x), x·e^x) ব্যবহৃত হয়। সাধারণত LIATE ক্রমে u বাছাই করা হয়।"
              : "Used for products of functions (e.g. x·sin(x), x·e^x). The LIATE rule helps pick u: Logarithm, Inverse-trig, Algebraic, Trig, Exponential."}
          </p>
        </section>
        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "ক্যালকুলাসের মৌলিক উপপাদ্য" : "Fundamental Theorem of Calculus"}</h2>
          <BlockMath math={"\\int_a^b f(x) \\, dx = F(b) - F(a)"} />
          <p className="mt-1 text-muted-foreground">
            {lang === "bn"
              ? "এখানে F(x) হলো f(x) এর যেকোনো অখণ্ডিত। নির্দিষ্ট অখণ্ডন এই উপপাদ্য থেকে গণনা করা হয়।"
              : "Here F(x) is any antiderivative of f(x). Definite integrals are evaluated using this theorem."}
          </p>
        </section>
        <section>
          <h2 className="mb-2 text-lg font-bold">{lang === "bn" ? "HSC শিক্ষার্থীদের জন্য" : "For HSC students"}</h2>
          <p className="text-muted-foreground">
            {lang === "bn"
              ? "HSC গণিত ২য় পত্রের অখণ্ডন অধ্যায়ের অধিকাংশ স্ট্যান্ডার্ড অখণ্ড্য রূপ এই টুলে কাজ করে। জটিল অংশভাগে অখণ্ডন বা প্রতিস্থাপনের সমস্যাগুলো নিজে চেষ্টা করে এখানে যাচাই করুন।"
              : "Most standard antiderivatives in the HSC Mathematics 2nd Paper integration chapter are supported. Solve harder by-parts or substitution problems by hand first, then verify here."}
          </p>
        </section>
      </article>

      {/* Related */}
      <section className="mt-8">
        <h2 className={cn("mb-3 text-base font-bold", lang === "bn" && "font-bn")}>
          {lang === "bn" ? "সম্পর্কিত ক্যালকুলেটর" : "Related calculators"}
        </h2>
        <div className="flex flex-wrap gap-2 text-sm">
          <Link to="/math-solver" className="rounded-lg border border-border bg-card px-3 py-1.5 hover:bg-accent/10">
            {lang === "bn" ? "ম্যাথ সলভার" : "Math Solver"}
          </Link>
          <Link to="/scientific-calculator" className="rounded-lg border border-border bg-card px-3 py-1.5 hover:bg-accent/10">
            {lang === "bn" ? "সায়েন্টিফিক ক্যালকুলেটর" : "Scientific Calculator"}
          </Link>
          <Link to="/graphing-calculator" className="rounded-lg border border-border bg-card px-3 py-1.5 hover:bg-accent/10">
            {lang === "bn" ? "গ্রাফিং ক্যালকুলেটর" : "Graphing Calculator"}
          </Link>
        </div>
      </section>
    </CalculatorLayout>
  );
}
